import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Building2, Mail, Phone, LayoutGrid, List, ArrowUpDown, ChevronRight } from "lucide-react";
import { AccountArchiveControls } from "@/components/admin/AccountArchiveControls";

export default function AdminClients() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const statusFilter = params.get("status") || "";
  const [clients, setClients] = useState([]);
  const [view, setView] = useState("card");
  const [showArchived, setShowArchived] = useState(false);
  const [sort, setSort] = useState({ key: "name", dir: "asc" });

  const load = React.useCallback(() => {
    api.get(`/clients${showArchived ? "?archived=true" : ""}`).then(({ data }) => setClients(data)).catch(() => {});
  }, [showArchived]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => (statusFilter ? clients.filter((c) => c.client_status === statusFilter) : clients),
    [clients, statusFilter]
  );

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      if (typeof a[sort.key] === "number") return sort.dir === "asc" ? a[sort.key] - b[sort.key] : b[sort.key] - a[sort.key];
      const av = (a[sort.key] ?? "").toString().toLowerCase();
      const bv = (b[sort.key] ?? "").toString().toLowerCase();
      return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return arr;
  }, [filtered, sort]);

  const columns = [
    { key: "name", label: "Name", sortable: true },
    { key: "firm_name", label: "Firm / Organization", sortable: true },
    { key: "email", label: "Email", sortable: true },
    { key: "account_status", label: "Account Status", sortable: true },
    { key: "active_orders", label: "Active Jobs", sortable: true },
    { key: "completed_orders", label: "Completed Jobs", sortable: true },
    { key: "total_orders", label: "Total Jobs", sortable: true },
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          {statusFilter === "pending_approval" ? (
            <>
              <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900" data-testid="clients-review-title">New Clients Awaiting Review</h2>
              <p className="text-slate-500">Count: <span className="font-semibold text-indigo-700" data-testid="clients-review-count">{filtered.length}</span> · clients submitted for ARPS approval.
                {" "}<button data-testid="clients-clear-filter" onClick={() => setParams(new URLSearchParams())} className="text-indigo-600 underline">View all clients</button>
              </p>
            </>
          ) : (
            <>
              <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Clients & law firms</h2>
              <p className="text-slate-500">Attorney and client accounts using ARPS.</p>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="clients-archived-toggle" onClick={() => setShowArchived((v) => !v)}
            className={`rounded-lg border px-3 py-1.5 text-sm font-semibold ${showArchived ? "border-amber-300 bg-amber-50 text-amber-800" : "border-slate-200 bg-white text-slate-600"}`}>
            {showArchived ? "Viewing archived" : "Archived"}
          </button>
          <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1">
            <button data-testid="view-toggle-card" onClick={() => setView("card")} className={`rounded-md p-1.5 ${view === "card" ? "bg-slate-900 text-white" : "text-slate-500"}`}><LayoutGrid className="h-4 w-4" /></button>
            <button data-testid="view-toggle-list" onClick={() => setView("list")} className={`rounded-md p-1.5 ${view === "list" ? "bg-slate-900 text-white" : "text-slate-500"}`}><List className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {view === "card" ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {sorted.map((c) => (
            <div key={c.id} data-testid={`client-card-${c.id}`} onClick={() => nav(`/admin/clients/${c.id}`)} title="Open client detail" className="group cursor-pointer rounded-xl border border-slate-200 bg-white p-5 transition hover:border-emerald-300 hover:shadow-md">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-100"><Building2 className="h-5 w-5 text-slate-700" /></div>
                <div className="min-w-0">
                  <button data-testid={`client-name-link-${c.id}`} onClick={(e) => { e.stopPropagation(); nav(`/admin/clients/${c.id}`); }} className="truncate text-left font-display text-base font-bold text-slate-900 underline-offset-2 group-hover:text-emerald-600 group-hover:underline">{c.name}</button>
                  <p className="truncate text-xs text-slate-500">{c.firm_name || "Independent"}</p>
                </div>
              </div>
              <div className="mt-4 space-y-1.5 text-sm text-slate-600">
                <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-slate-400" /> {c.email}</p>
                {c.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-400" /> {c.phone}</p>}
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-sky-50 px-2 py-2"><span className="font-display text-lg font-bold text-sky-700">{c.active_orders}</span><p className="text-[11px] text-slate-500">Active</p></div>
                <div className="rounded-lg bg-emerald-50 px-2 py-2"><span className="font-display text-lg font-bold text-emerald-700">{c.completed_orders}</span><p className="text-[11px] text-slate-500">Completed</p></div>
                <div className="rounded-lg bg-slate-50 px-2 py-2"><span className="font-display text-lg font-bold text-slate-900">{c.total_orders}</span><p className="text-[11px] text-slate-500">Total</p></div>
              </div>
              <Button data-testid={`open-client-detail-${c.id}`} size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); nav(`/admin/clients/${c.id}`); }} className="mt-3 w-full">{statusFilter === "pending_approval" ? "Review" : "View detail"} <ChevronRight className="ml-1 h-3.5 w-3.5" /></Button>
              <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                <AccountArchiveControls id={c.id} name={c.name} role="client" archived={showArchived} onDone={load} />
              </div>
            </div>
          ))}
          {sorted.length === 0 && <p className="col-span-full py-12 text-center text-slate-400">{statusFilter === "pending_approval" ? "No clients awaiting review." : "No clients registered yet."}</p>}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="client-list-view">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                {columns.map((col) => (
                  <th key={col.key} className="px-4 py-3">
                    {col.sortable ? (
                      <button data-testid={`sort-${col.key}`} onClick={() => toggleSort(col.key)} className="flex items-center gap-1 hover:text-slate-800">
                        {col.label} <ArrowUpDown className="h-3 w-3" />{sort.key === col.key && <span className="text-emerald-600">{sort.dir === "asc" ? "↑" : "↓"}</span>}
                      </button>
                    ) : col.label}
                  </th>
                ))}
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`} className="cursor-pointer border-t border-slate-50 hover:bg-slate-50" onClick={() => nav(`/admin/clients/${c.id}`)}>
                  <td className="px-4 py-3">
                    <button data-testid={`client-name-link-${c.id}`} onClick={(e) => { e.stopPropagation(); nav(`/admin/clients/${c.id}`); }} className="font-semibold text-slate-900 hover:text-emerald-600 hover:underline">{c.name}</button>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{c.firm_name || "Independent"}</td>
                  <td className="px-4 py-3 text-slate-600">{c.email}</td>
                  <td className="px-4 py-3"><span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Active</span></td>
                  <td className="px-4 py-3 text-slate-700">{c.active_orders}</td>
                  <td className="px-4 py-3 text-slate-700">{c.completed_orders}</td>
                  <td className="px-4 py-3 text-slate-700">{c.total_orders}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <AccountArchiveControls id={c.id} name={c.name} role="client" archived={showArchived} onDone={load} />
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">{statusFilter === "pending_approval" ? "No clients awaiting review." : "No clients registered yet."}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
